import { direLaLangueAuTelephone } from '../native/langue';
import { ar } from './ar';
import { en } from './en';
import { es } from './es';
import { fr } from './fr';
import { zh } from './zh';
import type { Dictionnaire } from './type';

/**
 * Les langues de l'application.
 *
 * # Une langue par lancement
 *
 * La langue se decide une fois, au chargement, et `t` est une constante :
 * on ecrit `t.fil.vide` partout, dans un ecran comme dans la couche des
 * donnees, sans crochet ni contexte. En changer dans les reglages
 * recharge la page, qui repart dans l'autre langue. Un choix qu'on fait
 * une fois dans la vie d'un compte ne justifiait pas de faire suivre la
 * langue a chaque composant.
 *
 * # Ajouter une langue
 *
 * Un dossier de plus a cote de `fr/` et des autres, type par `Dictionnaire` :
 * la compilation dit ce qui manque. Puis une ligne dans `DICTIONNAIRES`
 * et dans `LANGUES`, et les fonctions (`functions/src/langues.ts`).
 */
export type Langue = 'fr' | 'en' | 'es' | 'zh' | 'ar';
/** Les langues traduites, a cote du francais qui fait foi dans la base. */
export type AutreLangue = Exclude<Langue, 'fr'>;
/** Ce que le membre a choisi : « automatique » suit le telephone. */
export type PreferenceDeLangue = 'auto' | Langue;
export type { Dictionnaire };

const DICTIONNAIRES: Record<Langue, Dictionnaire> = { fr, en, es, zh, ar };

/** Chaque langue dans la sienne : c'est ainsi qu'on la reconnait dans une liste. */
export const LANGUES: { code: Langue; nom: string }[] = [
  { code: 'fr', nom: 'Français' },
  { code: 'en', nom: 'English' },
  { code: 'es', nom: 'Español' },
  { code: 'zh', nom: '中文' },
  { code: 'ar', nom: 'العربية' },
];

/** Celles que la base garde a cote du francais (`en`, `es`, `zh`, `ar`). */
export const AUTRES_LANGUES = LANGUES.map((choix) => choix.code).filter(
  (code): code is AutreLangue => code !== 'fr',
);

/** Une chaine est-elle l'une de nos langues ? */
export function estUneLangue(code: string | null | undefined): code is Langue {
  return LANGUES.some((choix) => choix.code === code);
}

/** Retenue d'un lancement a l'autre, avant meme la session. */
const CLE = 'sirocco:langue';

export function preferenceDeLangue(): PreferenceDeLangue {
  try {
    const retenue = globalThis.localStorage?.getItem(CLE);
    if (estUneLangue(retenue)) return retenue;
  } catch {
    // Stockage refuse : on suit le telephone.
  }
  return 'auto';
}

/**
 * La langue du telephone, ramenee a celles qu'on parle.
 *
 * On parcourt toute la liste de ses langues, pas seulement la premiere :
 * un telephone regle en arabe avec le francais en second parle francais
 * ici. Aucune ne correspond : le francais, la langue de ceux pour qui
 * l'application existe, et celle que ces membres lisaient jusqu'ici.
 */
export function langueDuTelephone(): Langue {
  try {
    const demandees = globalThis.navigator?.languages?.length
      ? globalThis.navigator.languages
      : [globalThis.navigator?.language ?? ''];
    for (const code of demandees) {
      const racine = code.slice(0, 2).toLowerCase();
      if (estUneLangue(racine)) return racine;
    }
  } catch {
    // Pas de navigateur (tests) : la langue d'origine.
  }
  return 'fr';
}

export const preference: PreferenceDeLangue = preferenceDeLangue();
export const langue: Langue = preference === 'auto' ? langueDuTelephone() : preference;
export const t: Dictionnaire = DICTIONNAIRES[langue];

/**
 * La locale des dates et des nombres.
 *
 * L'anglais britannique plutot qu'americain : jour avant mois et heure
 * sur vingt-quatre, comme en francais. Un membre qui passe d'une langue
 * a l'autre relit les memes dates dans le meme ordre. L'arabe garde les
 * chiffres latins et l'heure sur vingt-quatre, comme au Maghreb.
 */
export const locale = {
  fr: 'fr-FR',
  en: 'en-GB',
  es: 'es-ES',
  zh: 'zh-CN',
  ar: 'ar-u-hc-h23-nu-latn',
}[langue];

/**
 * Le sens de lecture. L'arabe se lit de droite a gauche : la page entiere
 * se retourne (`dir="rtl"`), et la feuille de style suit par ses
 * proprietes logiques et `:root[dir='rtl']`.
 */
export const sens: 'ltr' | 'rtl' = langue === 'ar' ? 'rtl' : 'ltr';

/**
 * Le meme, en facteur pour les gestes : 1 quand on avance vers la droite,
 * -1 quand on avance vers la gauche. Un balayage « vers l'avant », un
 * ecran qui entre « par la fin » se multiplient par lui.
 */
export const sensX: 1 | -1 = sens === 'rtl' ? -1 : 1;

try {
  document.documentElement.lang = langue;
  document.documentElement.dir = sens;
} catch {
  // Pas de document (tests).
}

/**
 * Change de langue : retenue, dite au telephone, puis la page repart
 * dans la nouvelle.
 */
export function changerDeLangue(suivante: PreferenceDeLangue): void {
  try {
    if (suivante === 'auto') localStorage.removeItem(CLE);
    else localStorage.setItem(CLE, suivante);
  } catch {
    return;
  }
  void direLaLangueAuTelephone(suivante).finally(() => window.location.reload());
}
